import {
  IAftnMessage,
  ISnowtam,
  ISnowtamRwy,
} from "@naviair-utm/node-shared-interfaces";
import { getStringFromRegExp, trimmer } from "../CommonParsers";
import {
  apronRegex,
  grfIcaoRegex,
  remarksRegex,
  grfSituationalAwarenessSection,
  grfDayTimeRegex,
} from "./SnowtamRegExp";
import * as parsers from "./SnowtamParsers";
import { newlineRegex } from "../CommonRegExp";
import moment from "moment";

/**
 * Par a snowtam message and output an ISnowtam object.
 * Works with both old and new snowtam format (implemented August 2021 in DK)
 * @param input
 * @returns
 */
export const snowtamParser = (input: string): IAftnMessage<ISnowtam> => {
  let snowtamParsed: IAftnMessage<ISnowtam> = {} as IAftnMessage<ISnowtam>;
  snowtamParsed.type = "SNOWTAM";
  snowtamParsed.parsedMessage = {} as ISnowtam;
  snowtamParsed.errors = [];

  // Replace multiple whitespaces with a single whitespace
  const snowtamCleaned = input.replace(newlineRegex, " ").replace(/\x0A/g, " ");
  const trimmedSnowtam = trimmer(snowtamCleaned);
  snowtamParsed.rawInput = trimmedSnowtam;

  // Get ICAO code from Snowtam
  let icao: string = getStringFromRegExp(trimmedSnowtam, grfIcaoRegex);
  if (icao && icao !== null) {
    snowtamParsed.icao = icao;
  } else {
    snowtamParsed.errors.push("No ICAO code parsed");
  }

  // Get date and time for the snowtam
  const dayTime = parsers.dateTimeParser(getStringFromRegExp(trimmedSnowtam, grfDayTimeRegex));
  if (dayTime) {
    snowtamParsed.validity = { start: dayTime };

    // Validity end is 8 hours after start if GRF format or 24 hours if old format
    snowtamParsed.validity.end = moment(dayTime)
      .add(8, "hours")
      .toDate();
  } else {
    snowtamParsed.errors.push("Failed parsing day and time for message");
  }

  // Get info for each runway (rwy) provided in snowtam
  const rwys: ISnowtamRwy[] = parsers.grfRwyParser(trimmedSnowtam)
  if (rwys.length > 0) {
    snowtamParsed.parsedMessage.runways = [];
    snowtamParsed.parsedMessage.runways = rwys;
  }

  // Get any information on apron provided
  const apron: string = getStringFromRegExp(trimmedSnowtam, apronRegex);
  if (apron !== null) {
    snowtamParsed.parsedMessage.apron = apron;
  }

  // Get next planned observation in dateTime format
  const nextPlannedObservation =
    parsers.nextPlannedObservationParser(trimmedSnowtam);
  if (nextPlannedObservation) {
    snowtamParsed.parsedMessage.nextPlannedObservation = nextPlannedObservation;
  }

  // Get any remarks written in plain text
  const remarks: string = getStringFromRegExp(trimmedSnowtam, remarksRegex);
  if (remarks !== null) {
    snowtamParsed.parsedMessage.remarks = remarks;
  }

  // Get situational awareness section and add to remarks (only GRF format)
  const saSection = getStringFromRegExp(input, grfSituationalAwarenessSection);
  if (saSection !== null) {
    snowtamParsed.parsedMessage.remarks = saSection;
  }

  return snowtamParsed;
};
